import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "../../utils/notify";
import { motion, AnimatePresence } from "framer-motion";
import TabNavigation from "../ui/TabNavigation";
import PermissionGate, { AdminGate, PlatformAdminGate } from "../PermissionGate";
import { useAuth } from "../../context/AuthContext";
import { useSnackbar } from "../../context/SnackbarContext";
import { Cog6ToothIcon, UsersIcon, UserIcon, KeyIcon, ShieldCheckIcon, UserPlusIcon, DocumentCheckIcon } from "@heroicons/react/24/outline";
import { fetchUsersApi, fetchConfigSettingsApi, fetchCompaniesApi, deleteUserApi, saveUserApi, resetUserPasswordApi } from "../../services/axios/AuthApis/AdministrationApis";
import { RoleColour, StatusColour } from "../../constants/AppEnums";
import CreateUserModal from "./CreateUserModal";
import RoleSelectionModal from "./RoleSelectionModal";
import SecurityTabContent from "./SecurityTab";
import PlatformSettings from "./PlatformSettings";
import ConsentSettings from "./ConsentSettings";
import UsersTabContent from "./UsersTabTableData";
import { Settings } from "lucide-react";
const blankUser = {
  firstname: "",
  lastname: "",
  email: "",
  role: "",
  department: "",
  phone: "",
  location: "",
  uid: "",
  status: "",
  customername: "",
  reporting_to: "",
  created_by: "",
  updated_by: "",
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  last_login: null, // Will be set on first actual login
  company: "",
  employeecount: "",
  price: "",
  renewal_date: "",
  logoImg: "",
  profile_photo: "",
  company_ids: []
};


const Administration = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [selectedTab, setSelectedTab] = useState("settings");

  // Determine allowed tabs based on user role
  const isAdmin = user?.role === 'platform_admin' || user?.role === 'admin';
  const [showRoleSelectionModal, setShowRoleSelectionModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const [activeTab, setActiveTab] = useState("users2");
  const [users2, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isViewMode, setIsViewMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [userPagination, setUserPagination] = useState({
    page: 1,
    page_size: 20,
    total_pages: 1,
    count: 0
  });
  useEffect(() => {
    if (location.state?.activeTab) {
      setSelectedTab(location.state.activeTab);
    }
  }, [location]);
  const [deleting, setDeleting] = useState(false);
  const [errors, setErrors] = useState({});
  const [newUser, setNewUser] = useState(blankUser);
  const [employeeConfig, setEmployeeConfig] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false)
  const [userFilters, setUserFilters] = useState({ email: "", username: "", role: "", company: "" });
  const [companyOptions, setCompanyOptions] = useState([]);
  const removeRow = (id) => {
    setUsers((prevUsers) => prevUsers.filter((user) => user.id !== id));
  };
  const roleColor = RoleColour;
  const getRoleColor = (role) => {
    const roleObj = roleColor.find(r => r.role === role);
    return roleObj ? roleObj.color : "text-gray-900 bg-gray-300 dark:text-gray-100 dark:bg-gray-700";
  };

  const getStatusColor = (status) => {
    return StatusColour.find(s => s.status === status)?.color || "text-gray-900 bg-gray-300 dark:text-gray-100 dark:bg-gray-700";
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "null") return "Never logged in";
    // Convert to local timezone explicitly
    const utcDate = new Date(dateString);
    const localDate = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000 * -1);

    const now = new Date();
    const diffMs = now - localDate;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return `${diffSec} seconds ago`;
    if (diffMin < 60) return `${diffMin} minutes ago`;
    if (diffHour < 24) return `${diffHour} hours ago`;
    if (diffDay < 7) return `${diffDay} days ago`;

    return localDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (!showCreateUserModal) {
      setNewUser(blankUser);
      setIsViewMode(false);
      setEditingUserId(null);
    }
  }, [showCreateUserModal]);

  // Canonical users-list refresh. The create/update-user backend response
  // shape differs by role (e.g. security_analyst/auditor return no user
  // payload at all, others nest it under "data") and never matches the flat
  // shape this GET endpoint returns, so the create/edit flow refreshes from
  // here instead of hand-inserting the raw API response into state.
  const refreshUsers = async () => {
    try {
      setLoading(true);

      const data = await fetchUsersApi(
        userPagination.page,
        userPagination.page_size,
        userFilters
      );

      setUsers(data.results || []);
      setUserPagination((p) => ({
        ...p,
        total_pages: data.total_pages,
        count: data.count,
      }));
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTab !== "users2") return;
    refreshUsers();
  }, [selectedTab, userPagination.page, userPagination.page_size, userFilters]);

  // Fetch company options for the filter dropdown
  useEffect(() => {
    const fetchCompanyOptions = async () => {
      try {
        const data = await fetchCompaniesApi("");
        setCompanyOptions((data.results || []).map((c) => ({ value: c.id, label: c.company_name })));
      } catch (error) {
        console.error("Error fetching company options:", error);
      }
    };
    fetchCompanyOptions();
  }, []);


  useEffect(() => {
    const fetchConfigSettings = async () => {
      try {
        const data = await fetchConfigSettingsApi();

        // ✅ Optional: filter if you only want specific type
        const employeeConfigs = data.filter(
          (item) => item.type?.toLowerCase() === "crm"
        );

        setEmployeeConfig(employeeConfigs);
      } catch (error) {
        console.error("Error fetching config settings:", error);
      }
    };
    fetchConfigSettings();
  }, []);


  useEffect(() => {
    // whenever users2 changes, sync to localStorage
    localStorage.setItem("admin_users", JSON.stringify(users2));
  }, [users2]);

  const handleAddUser = () => {
    // Reset everything first
    setIsViewMode(false);
    setEditingUserId(null);
    setErrors({});
    setNewUser(blankUser);

    // Show role selection modal
    setShowRoleSelectionModal(true);
  };


  const handleViewUser = (user) => {
    setIsViewMode(true); // read-only
    setNewUser({
      firstname: user.firstname || "",
      lastname: user.lastname || "",
      email: user.email || "",
      role: user.role || "end_user",
      department: user.department || "",
      phone: user.phone || "",
      location: user.location || "",
      uid: user.uid || "",
      status: user.status || "active",
      customername: user.customername || "",
      reporting_to: user.reporting_to || "",
      created_by: user.created_by || "",
      updated_by: user.updated_by || "",
      created_at: user.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      last_login: user.last_login || null,
      company: user.company || "",
      employeecount: user.employeecount || "",
      price: user.price || "",
      date_of_joining: user.date_of_joining || "",
      renewal_date: user.renewal_date || "",
      logoImg: user.logo_img || "",
      profile_photo: user.profile_photo || ""
    });
    setShowCreateUserModal(true);
  };

  const handleEditUser = (user) => {
    setIsViewMode(false); // editable
    setEditingUserId(user.id); // store which user we are editing
    setNewUser({
      id: user.id,
      firstname: user.firstname || "",
      lastname: user.lastname || "",
      email: user.email || "",
      role: user.role || "end_user",
      department: user.department || "",
      phone: user.phone || "",
      location: user.location || "",
      uid: user.uid || "",
      status: user.status || "active",
      customername: user.customername || "",
      reporting_to: user.reporting_to || "",
      created_by: user.created_by || "",
      updated_by: user.updated_by || "",
      created_at: user.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      last_login: user.last_login || null,
      company: user.company || "",
      employeecount: user.employeecount || "",
      price: user.price || "",
      date_of_joining: user.date_of_joining || "",
      renewal_date: user.renewal_date || "",
      logoImg: user.logo_img || "",
      profile_photo: user.profile_photo || ""
    });
    setShowCreateUserModal(true);
  };


  const handleResetPassword = async (user) => {
    const confirmed = window.confirm(
      `Reset the password for ${user.email}?\n\n` +
      "Their current password stops working and they are emailed a code to choose a new one."
    );
    if (!confirmed) return;
    try {
      const data = await resetUserPasswordApi(user.id);
      showSnackbar(data?.message || "Password reset started.", "success", { duration: 7000 });
    } catch (err) {
      showSnackbar(
        err.response?.data?.error || err.message || "Failed to reset the password.",
        "error",
        { duration: 7000 },
      );
    }
  };

  const handleDeleteUser = async (userId, removeRow) => {
    try {
      const data = await deleteUserApi(userId);
      toast.success(data?.message || "User deleted successfully");
      removeRow(userId);
    } catch (err) {
      console.error("❌ Delete error:", err);
      toast.error(
        err.response?.data?.error ||
        err.message ||
        "Failed to delete user."
      );
    }
  };

  return (
    <div className="w-full">
      <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto bg-white dark:bg-[#0f172a] min-h-screen">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 md:mb-6 gap-4">
            <div className="flex items-center space-x-4">
              <div>
                <Settings className="w-6 md:w-8 h-6 md:h-8 text-blue-600" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white truncate">{isAdmin ? "Administration" : "Settings"}</h1>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 truncate md:truncate-none">
                  {isAdmin ? "Manage platform settings, users, and organizational configurations" : "Manage your platform settings"}
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* Horizontal Tab Navigation */}
        {(() => {
          const tabs = [
            { id: 'settings', name: 'Platform Settings', icon: Cog6ToothIcon },
            ...(user?.role !== 'platform_admin' && user?.role !== 'admin' ? [
              { id: 'consentSettings', name: 'Consent Settings', icon: DocumentCheckIcon }
            ] : []),
            ...(user?.role === 'platform_admin' || user?.role === 'admin' ? [
              { id: 'users2', name: 'User Management', icon: UsersIcon },
              // { id: 'security', name: 'Security', icon: ShieldCheckIcon },
            ] : []),
          ];

          return (
            <>
              <TabNavigation
                tabs={tabs}
                activeTab={selectedTab}
                onTabChange={setSelectedTab}
              />

              <AnimatePresence mode="wait">
                {selectedTab === 'settings' && (
                  <motion.div key="settings" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                    <PlatformSettings user={user} />
                  </motion.div>
                )}
                {selectedTab === 'consentSettings' && (
                  <motion.div key="consentSettings" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                    <ConsentSettings user={user} />
                  </motion.div>
                )}
                {selectedTab === 'users2' && (
                  <motion.div key="users2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                    <UsersTabContent
                      users2={users2}
                      getRoleColor={getRoleColor}
                      getStatusColor={getStatusColor}
                      handleViewUser={handleViewUser}
                      handleEditUser={handleEditUser}
                      handleDeleteUser={handleDeleteUser}
                      handleResetPassword={handleResetPassword}
                      formatDate={formatDate}
                      userPagination={userPagination}
                      setUserPagination={setUserPagination}
                      loading={loading}
                      setShowRoleSelectionModal={setShowRoleSelectionModal}
                      removeRow={removeRow}
                      userFilters={userFilters}
                      setUserFilters={setUserFilters}
                      companyOptions={companyOptions}
                    />
                  </motion.div>
                )}
                {/* {selectedTab === 'security' && (
                  <motion.div key="security" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                    <SecurityTabContent loading={loading} />
                  </motion.div>
                )} */}
              </AnimatePresence>
            </>
          );
        })()}

        {/* Role Selection Modal */}
        <AnimatePresence>
          {showRoleSelectionModal && (
            <RoleSelectionModal
              setShowRoleSelectionModal={setShowRoleSelectionModal}
              setNewUser={setNewUser}
              blankUser={blankUser}
              setShowCreateUserModal={setShowCreateUserModal}
            />
          )}
        </AnimatePresence>

        {/* User Creation Modal */}
        <AnimatePresence>
          {showCreateUserModal && (
            <CreateUserModal
              setShowCreateUserModal={setShowCreateUserModal}
              newUser={newUser}
              setNewUser={setNewUser}
              errors={errors}
              setErrors={setErrors}
              isViewMode={isViewMode}
              editingUserId={editingUserId}
              employeeConfig={employeeConfig}
              showDropdown={showDropdown}
              setShowDropdown={setShowDropdown}
              setIsViewMode={setIsViewMode}
              setEditingUserId={setEditingUserId}
              blankUser={blankUser}
              setShowRoleSelectionModal={setShowRoleSelectionModal}
              getRoleColor={getRoleColor}
              userPagination={userPagination}
              setUserPagination={setUserPagination}
              refreshUsers={refreshUsers}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Administration;